import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import type {
  ExpenseCategory,
  ExpenseSource,
  SolTemporalKpiSnapshot,
} from '@economi/domain-contracts';
import {
  centsToSoles,
  classifyExpenseHorizonForCardInCycle,
  computeExpenseAnalytics,
  EXPENSE_CATEGORY_LABELS,
  formatSolesFromCents,
  isDateWithinPeriod,
  isExpenseInevitableInState,
  resolveCardBillingCloseDateForCycle,
  resolveExpenseReceiptImageUrl,
  simulateExpenseImpact,
  type BillingCycleHorizon,
  type ExpenseLedgerItem,
  type FinancialCycleState,
  type PaymentMethodType,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './ExpensesAnalyticsView.module.css';

interface ExpensesAnalyticsViewProps {
  readonly cycleState: FinancialCycleState;
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly onAddExpense?: (params: {
    amountSoles: number;
    merchantName: string;
    issuerRuc?: string | null;
    category: ExpenseCategory;
    description: string;
    source: ExpenseSource;
    occurredAtIso?: string;
    isInevitable?: boolean;
    paymentMethod?: PaymentMethodType;
    creditCardId?: string | null;
    cardLastFourDigits?: string | null;
    billingCycleHorizon?: BillingCycleHorizon;
  }) => void;
  readonly onUpdateExpense?: (
    expenseId: string,
    updates: {
      merchantName?: string;
      issuerRuc?: string | null;
      receiptNumber?: string | null;
      occurredAtIso?: string;
      category?: ExpenseCategory;
      description?: string;
      amountSoles?: number;
      isInevitable?: boolean;
      creditCardId?: string | null;
      cardLastFourDigits?: string | null;
    }
  ) => void;
  readonly onUpdateExpenseDate?: (
    expenseId: string,
    nextOccurredAtIso: string
  ) => void;
  readonly onAttachExpenseReceiptImage?: (
    expenseId: string,
    receiptImageDataUrl: string | null
  ) => void;
  readonly onOpenScannerView?: () => void;
  readonly onDeleteExpense?: (expenseId: string) => void;
}

function compressFileToReceiptDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error('No se pudo leer el archivo de imagen'));
    reader.onload = () => {
      const rawDataUrl =
        typeof reader.result === 'string' ? reader.result : '';
      if (!rawDataUrl) {
        reject(new Error('Archivo de imagen vacío'));
        return;
      }
      const img = new Image();
      img.onload = () => {
        try {
          const maxDim = 1100;
          let width = img.naturalWidth || img.width || 800;
          let height = img.naturalHeight || img.height || 1000;
          if (width > maxDim || height > maxDim) {
            if (width >= height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(rawDataUrl);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        } catch {
          resolve(rawDataUrl);
        }
      };
      img.onerror = () => resolve(rawDataUrl);
      img.src = rawDataUrl;
    };
    reader.readAsDataURL(file);
  });
}

function formatSourceLabel(
  source: ExpenseSource,
  hasReceiptImage: boolean
): string {
  switch (source) {
    case 'RECEIPT_OCR':
      return hasReceiptImage
        ? '🧾 Boleta / Recibo Escaneado (Con Imagen)'
        : '🧾 Boleta / Recibo Escaneado';
    case 'RECURRING_RULE':
      return '🔄 Gasto Recurrente Auto-Descontado';
    case 'PUBLIC_API':
      return '🔌 API REST v1';
    case 'WEB_MANUAL':
      return hasReceiptImage
        ? '✍️ Manual (Con Imagen Adjunta)'
        : '✍️ Manual (Sin Imagen)';
  }
}

export function ExpensesAnalyticsView({
  cycleState,
  snapshot,
  onAddExpense,
  onUpdateExpense,
  onUpdateExpenseDate,
  onAttachExpenseReceiptImage,
  onOpenScannerView,
  onDeleteExpense,
}: ExpensesAnalyticsViewProps) {
  // Selector de Período por Fecha de Emisión (occurredAtIso)
  const [selectedPeriodKey, setSelectedPeriodKey] =
    useState<string>('ACTIVE_PERIOD');

  // Visor Modal de Detalle del Gasto y Boleta Escaneada
  const [selectedDetailExpenseId, setSelectedDetailExpenseId] = useState<
    string | null
  >(null);
  const [isReceiptZoomed, setIsReceiptZoomed] = useState<boolean>(false);
  const [failedReceiptImageIds, setFailedReceiptImageIds] = useState<
    Record<string, boolean>
  >({});
  const [attachingImageExpenseId, setAttachingImageExpenseId] = useState<
    string | null
  >(null);

  // Estado de edición completa del gasto dentro de la Ficha Detallada
  const [isEditingDetailModal, setIsEditingDetailModal] =
    useState<boolean>(false);
  const [editMerchantName, setEditMerchantName] = useState<string>('');
  const [editIssuerRuc, setEditIssuerRuc] = useState<string>('');
  const [editReceiptNumber, setEditReceiptNumber] = useState<string>('');
  const [editOccurredAtIso, setEditOccurredAtIso] = useState<string>('');
  const [editCategory, setEditCategory] =
    useState<ExpenseCategory>('FOOD_DINING');
  const [editIsInevitable, setEditIsInevitable] = useState<boolean>(false);
  const [editCreditCardId, setEditCreditCardId] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [editAmountSoles, setEditAmountSoles] = useState<string>('');

  // Meses adicionales detectados en gastos cuya fecha de emisión está fuera del período activo
  const externalMonths = useMemo(() => {
    const monthsSet = new Set<string>();
    for (const exp of cycleState.expenses) {
      const dateStr = exp.occurredAtIso.slice(0, 10);
      if (
        !isDateWithinPeriod(
          dateStr,
          cycleState.startDateIso,
          cycleState.renewalDateIso
        )
      ) {
        const monthIso = dateStr.slice(0, 7);
        if (monthIso.length === 7) {
          monthsSet.add(monthIso);
        }
      }
    }
    return Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
  }, [cycleState.expenses, cycleState.startDateIso, cycleState.renewalDateIso]);

  const effectivePeriodRange = useMemo<
    { startDateIso: string; endDateIso: string } | 'ALL'
  >(() => {
    if (selectedPeriodKey === 'ALL') {
      return 'ALL';
    }
    if (selectedPeriodKey.startsWith('MONTH:')) {
      const monthIso = selectedPeriodKey.replace('MONTH:', '');
      return {
        startDateIso: `${monthIso}-01`,
        endDateIso: `${monthIso}-31`,
      };
    }
    return {
      startDateIso: cycleState.startDateIso.slice(0, 10),
      endDateIso: cycleState.renewalDateIso.slice(0, 10),
    };
  }, [selectedPeriodKey, cycleState.startDateIso, cycleState.renewalDateIso]);

  const analytics = useMemo(
    () => computeExpenseAnalytics(cycleState, snapshot, effectivePeriodRange),
    [cycleState, snapshot, effectivePeriodRange]
  );

  // Estado del Formulario de Registro Manual Sin Imagen (plegado por defecto para ver el listado de inmediato)
  const [showManualForm, setShowManualForm] = useState<boolean>(false);
  const [manualMerchant, setManualMerchant] = useState<string>('');
  const [manualAmountSoles, setManualAmountSoles] = useState<string>('');
  const [manualOccurredAtIso, setManualOccurredAtIso] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [manualCategory, setManualCategory] =
    useState<ExpenseCategory>('FOOD_DINING');
  const [manualIsInevitable, setManualIsInevitable] = useState<boolean>(false);
  const [manualCardSelection, setManualCardSelection] = useState<string>('');
  const [manualRuc, setManualRuc] = useState<string>('');
  const [manualDescription, setManualDescription] = useState<string>('');

  // Estado para edición rápida de Monto y Fecha de Emisión en la lista
  const [editingExpenseDateId, setEditingExpenseDateId] = useState<
    string | null
  >(null);
  const [editingDateValue, setEditingDateValue] = useState<string>('');
  const [editingAmountValue, setEditingAmountValue] = useState<string>('');

  const [selectedProductKey, setSelectedProductKey] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [controlFilter, setControlFilter] = useState<
    'ALL' | 'CONTROLLABLE_ONLY' | 'INEVITABLE_ONLY'
  >('ALL');
  const [sourceFilter, setSourceFilter] = useState<
    'ALL' | 'WITH_RECEIPT_IMAGE' | 'MANUAL_AND_RECURRING'
  >('ALL');
  const [cardFilter, setCardFilter] = useState<string>('ALL');
  const [horizonFilter, setHorizonFilter] = useState<
    'ALL' | 'CURRENT_CYCLE' | 'NEXT_CYCLE'
  >('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const configuredCards = cycleState.creditCards ?? [];

  const topCategory = analytics.byCategory[0] ?? null;
  const topMerchant = analytics.byMerchant[0] ?? null;
  const topProduct = analytics.byProductOrService[0] ?? null;

  const activeProduct =
    analytics.byProductOrService.find((p) => p.itemKey === selectedProductKey) ??
    topProduct;

  const filteredExpenses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return analytics.filteredExpenses.filter((exp) => {
      const inevitable = isExpenseInevitableInState(
        exp,
        cycleState.recurringExpenses
      );
      if (controlFilter === 'CONTROLLABLE_ONLY' && inevitable) {
        return false;
      }
      if (controlFilter === 'INEVITABLE_ONLY' && !inevitable) {
        return false;
      }
      if (categoryFilter !== 'ALL' && exp.category !== categoryFilter) {
        return false;
      }
      if (cardFilter === 'DIRECT_DEBIT_OR_CASH' && exp.creditCardId) {
        return false;
      }
      if (
        cardFilter !== 'ALL' &&
        cardFilter !== 'DIRECT_DEBIT_OR_CASH' &&
        exp.creditCardId !== cardFilter
      ) {
        return false;
      }
      const expHorizon = exp.billingCycleHorizon ?? 'CURRENT_CYCLE';
      if (horizonFilter !== 'ALL' && expHorizon !== horizonFilter) {
        return false;
      }
      const hasReceipt =
        Boolean(resolveExpenseReceiptImageUrl(exp)) ||
        exp.source === 'RECEIPT_OCR';
      if (sourceFilter === 'WITH_RECEIPT_IMAGE' && !hasReceipt) {
        return false;
      }
      if (sourceFilter === 'MANUAL_AND_RECURRING' && hasReceipt) {
        return false;
      }
      if (!q) return true;
      const inMerchant = exp.merchantName.toLowerCase().includes(q);
      const inRuc = (exp.issuerRuc ?? '').toLowerCase().includes(q);
      const inReceiptNum = (exp.receiptNumber ?? '').toLowerCase().includes(q);
      const inDesc = exp.description.toLowerCase().includes(q);
      const inItems = (exp.lineItems ?? []).some((li) =>
        li.description.toLowerCase().includes(q)
      );
      return inMerchant || inRuc || inReceiptNum || inDesc || inItems;
    });
  }, [
    analytics.filteredExpenses,
    cycleState.recurringExpenses,
    categoryFilter,
    controlFilter,
    sourceFilter,
    cardFilter,
    horizonFilter,
    searchQuery,
  ]);

  const selectedDetailExpense = useMemo<ExpenseLedgerItem | null>(
    () =>
      cycleState.expenses.find((exp) => exp.id === selectedDetailExpenseId) ??
      null,
    [cycleState.expenses, selectedDetailExpenseId]
  );

  const openExpenseDetailModal = (
    exp: ExpenseLedgerItem,
    startInEditMode = false
  ) => {
    setSelectedDetailExpenseId(exp.id);
    setIsReceiptZoomed(false);
    setEditMerchantName(exp.merchantName);
    setEditIssuerRuc(exp.issuerRuc ?? '');
    setEditReceiptNumber(exp.receiptNumber ?? '');
    setEditOccurredAtIso(exp.occurredAtIso.slice(0, 10));
    setEditCategory(exp.category);
    setEditIsInevitable(
      isExpenseInevitableInState(exp, cycleState.recurringExpenses)
    );
    setEditCreditCardId(exp.creditCardId ?? '');
    setEditDescription(exp.description ?? '');
    setEditAmountSoles(String(centsToSoles(exp.amountCents)));
    setIsEditingDetailModal(startInEditMode);
  };

  const parsePositiveSoles = (raw: string, fallbackSoles: number): number => {
    const cleaned = String(raw ?? '')
      .trim()
      .replace(/[^0-9.,]/g, '')
      .replace(',', '.');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) && parsed > 0
      ? Math.max(0.01, parsed)
      : Math.max(0.01, fallbackSoles);
  };

  const handleSaveExpenseDetailEdits = () => {
    if (!selectedDetailExpense || !onUpdateExpense) return;
    const nextAmount = parsePositiveSoles(
      editAmountSoles,
      centsToSoles(selectedDetailExpense.amountCents)
    );
    const nextCardId = editCreditCardId.trim() || null;
    const pickedCard = nextCardId
      ? configuredCards.find((c) => c.id === nextCardId) ?? null
      : null;

    onUpdateExpense(selectedDetailExpense.id, {
      merchantName:
        editMerchantName.trim() || selectedDetailExpense.merchantName,
      issuerRuc: editIssuerRuc.trim() || null,
      receiptNumber: editReceiptNumber.trim() || null,
      occurredAtIso:
        editOccurredAtIso.slice(0, 10) ||
        selectedDetailExpense.occurredAtIso.slice(0, 10),
      category: editCategory,
      isInevitable: editIsInevitable,
      creditCardId: nextCardId,
      cardLastFourDigits:
        pickedCard?.lastFourDigits ||
        selectedDetailExpense.cardLastFourDigits ||
        null,
      description: editDescription.trim(),
      amountSoles: nextAmount,
    });
    setEditAmountSoles(String(nextAmount));
    setIsEditingDetailModal(false);
  };

  const handleAttachFileToExpense = async (
    expenseId: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !onAttachExpenseReceiptImage) return;
    setAttachingImageExpenseId(expenseId);
    try {
      const compressedDataUrl = await compressFileToReceiptDataUrl(file);
      onAttachExpenseReceiptImage(expenseId, compressedDataUrl);
      setFailedReceiptImageIds((prev) => ({
        ...prev,
        [expenseId]: false,
      }));
    } catch {
      // Ignoramos error silencioso si la imagen no pudo comprimirse
    } finally {
      setAttachingImageExpenseId(null);
      event.target.value = '';
    }
  };

  const numericManualSoles = Math.max(0, Number(manualAmountSoles) || 0);
  const manualImpact = simulateExpenseImpact(snapshot, numericManualSoles);
  const isManualDateInActivePeriod = isDateWithinPeriod(
    manualOccurredAtIso,
    cycleState.startDateIso,
    cycleState.renewalDateIso
  );
  const selectedManualCard = manualCardSelection
    ? configuredCards.find((c) => c.id === manualCardSelection) ?? null
    : null;
  const manualHorizon: BillingCycleHorizon = classifyExpenseHorizonForCardInCycle(
    cycleState,
    manualOccurredAtIso,
    selectedManualCard?.id ?? null
  );
  const selectedManualCardCloseDateIso = selectedManualCard
    ? resolveCardBillingCloseDateForCycle(
        selectedManualCard.billingCloseDayOfMonth,
        cycleState.startDateIso,
        cycleState.renewalDateIso
      )
    : null;

  const handleManualSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!onAddExpense || numericManualSoles <= 0) return;

    const cleanDate =
      manualOccurredAtIso.trim().slice(0, 10) ||
      cycleState.referenceDateIso.slice(0, 10);

    onAddExpense({
      amountSoles: numericManualSoles,
      merchantName: manualMerchant.trim() || 'Gasto Manual Directo',
      issuerRuc: manualRuc.trim() || null,
      category: manualCategory,
      description:
        manualDescription.trim() ||
        `Gasto manual (${EXPENSE_CATEGORY_LABELS[manualCategory]})`,
      source: 'WEB_MANUAL',
      occurredAtIso: cleanDate,
      isInevitable: manualIsInevitable,
      paymentMethod: 'CREDIT_CARD',
      creditCardId: selectedManualCard?.id ?? null,
      cardLastFourDigits: selectedManualCard?.lastFourDigits || null,
      billingCycleHorizon: manualHorizon,
    });

    // Si la fecha ingresada pertenece a otro mes, cambiamos el filtro para que el usuario lo vea de inmediato
    if (
      !isDateWithinPeriod(
        cleanDate,
        cycleState.startDateIso,
        cycleState.renewalDateIso
      )
    ) {
      setSelectedPeriodKey(`MONTH:${cleanDate.slice(0, 7)}`);
    }

    setManualMerchant('');
    setManualAmountSoles('');
    setManualRuc('');
    setManualDescription('');
    setManualIsInevitable(false);
  };

  // Construcción de segmentos SVG Donut para Gastos Controlables por Categoría (Armonía Sapphire Institutional Clarity)
  const sapphireCategoryPalette = [
    '#00236f',
    '#006a61',
    '#004469',
    '#2563eb',
    '#0d9488',
    '#4059aa',
    '#ba1a1a',
    '#757682',
  ];
  const donutRadius = 52;
  const donutCircumference = 2 * Math.PI * donutRadius;
  let cumulativePercent = 0;
  const donutSegments = analytics.byCategory.map((cat, idx) => {
    const fraction = Math.max(0, cat.shareOfTotalSpentPercent / 100);
    const dashLength = fraction * donutCircumference;
    const dashOffset = -cumulativePercent * donutCircumference;
    cumulativePercent += fraction;
    const colorHex =
      sapphireCategoryPalette[idx % sapphireCategoryPalette.length] ??
      cat.colorHex;
    return {
      category: cat.category,
      colorHex,
      dashArray: `${dashLength} ${Math.max(0, donutCircumference - dashLength)}`,
      dashOffset,
    };
  });

  const periodLabelText =
    selectedPeriodKey === 'ALL'
      ? 'Todos los períodos registrados'
      : selectedPeriodKey.startsWith('MONTH:')
        ? `Período ${selectedPeriodKey.replace('MONTH:', '')} (por fecha de emisión)`
        : `Período Activo (${cycleState.startDateIso.slice(0, 10)} al ${cycleState.renewalDateIso.slice(0, 10)})`;

  return (
    <div className={styles.container}>
      {/* A. Barra Superior de Agrupación por Período de Fecha de Emisión */}
      <section className={styles.periodSelectorBar}>
        <div className={styles.periodSelectorInfo}>
          <span className={styles.periodSelectorTitle}>
            📅 Período:{' '}
            <InfoPopover title="Agrupación por Fecha de Emisión">
              Los gastos e indicadores se agrupan por la fecha real de emisión del comprobante, sin importar qué día subiste la foto.
            </InfoPopover>
          </span>
        </div>

        <div className={styles.periodControlsGroup}>
          <select
            value={selectedPeriodKey}
            onChange={(e) => setSelectedPeriodKey(e.target.value)}
            className={styles.periodSelect}
            aria-label="Seleccionar período por fecha de emisión"
          >
            <option value="ACTIVE_PERIOD">
              📅 Período Activo ({cycleState.startDateIso.slice(0, 10)} al{' '}
              {cycleState.renewalDateIso.slice(0, 10)})
            </option>
            {externalMonths.map((monthIso) => (
              <option key={monthIso} value={`MONTH:${monthIso}`}>
                📂 Período {monthIso}
              </option>
            ))}
            <option value="ALL">
              🗂️ Todos ({cycleState.expenses.length} gastos)
            </option>
          </select>
        </div>
      </section>

      {/* B. Registro Manual de Gasto Sin Imagen (Visible en Celular y Computadora) */}
      {onAddExpense && (
        <section
          className={styles.manualExpenseCard}
          aria-label="Registrar Gasto Manual Sin Imagen"
        >
          <div className={styles.manualCardHeader}>
            <div>
              <h3 className={styles.manualCardTitle}>
                ✍️ Registrar Gasto Manual{' '}
                <InfoPopover title="Registro Manual sin Imagen">
                  Registra cualquier gasto en efectivo, transferencia o tarjeta indicando su fecha real de emisión. Aparecerá de inmediato en el listado del período correspondiente.
                </InfoPopover>
              </h3>
            </div>
            <button
              type="button"
              className={styles.toggleManualFormBtn}
              onClick={() => setShowManualForm((prev) => !prev)}
            >
              {showManualForm
                ? '🔼 Ocultar'
                : '➕ Nuevo Gasto Manual'}
            </button>
          </div>

          {showManualForm && (
            <form className={styles.manualFormGrid} onSubmit={handleManualSubmit}>
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  Comercio / Establecimiento o Concepto
                </label>
                <input
                  type="text"
                  placeholder="Ej. Mercado Central, Taxi, Almuerzo, Farmacia"
                  value={manualMerchant}
                  onChange={(e) => setManualMerchant(e.target.value)}
                  className={styles.manualInput}
                  required
                />
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>Monto en Soles (PEN)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Ej. 18.50"
                  value={manualAmountSoles}
                  onChange={(e) => setManualAmountSoles(e.target.value)}
                  className={styles.manualInput}
                  required
                />
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  📅 Fecha de Emisión / Gasto
                </label>
                <input
                  type="date"
                  value={manualOccurredAtIso}
                  onChange={(e) => setManualOccurredAtIso(e.target.value)}
                  className={styles.manualInput}
                  required
                />
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>Categoría</label>
                <select
                  value={manualCategory}
                  onChange={(e) =>
                    setManualCategory(e.target.value as ExpenseCategory)
                  }
                  className={styles.manualSelect}
                >
                  {(
                    Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]
                  ).map((catKey) => (
                    <option key={catKey} value={catKey}>
                      {EXPENSE_CATEGORY_LABELS[catKey]}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  💳 Tarjeta de Crédito Utilizada (incluye Yape / Plin con cargo a tarjeta)
                </label>
                <select
                  value={manualCardSelection}
                  onChange={(e) => setManualCardSelection(e.target.value)}
                  className={styles.manualSelect}
                >
                  <option value="">
                    — Selecciona la tarjeta de crédito usada —
                  </option>
                  {configuredCards.map((card) => (
                    <option key={card.id} value={card.id}>
                      💳 {card.alias} ({card.issuerBank}
                      {card.lastFourDigits ? ` · *${card.lastFourDigits}` : ''}) · Factura día{' '}
                      {card.billingCloseDayOfMonth}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  Clasificación de Control
                </label>
                <select
                  value={manualIsInevitable ? 'INEVITABLE' : 'CONTROLLABLE'}
                  onChange={(e) =>
                    setManualIsInevitable(e.target.value === 'INEVITABLE')
                  }
                  className={styles.manualSelect}
                >
                  <option value="CONTROLLABLE">
                    🎯 Gasto Manejable / Controlable (Sí mide en rankings)
                  </option>
                  <option value="INEVITABLE">
                    🔒 Gasto Inevitable (Suma al total, excluido de rankings)
                  </option>
                </select>
              </div>

              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  RUC o Nota Adicional (Opcional)
                </label>
                <div className={styles.manualInlineInputs}>
                  <input
                    type="text"
                    placeholder="RUC (opcional)"
                    value={manualRuc}
                    onChange={(e) => setManualRuc(e.target.value)}
                    className={styles.manualInputSmall}
                  />
                  <input
                    type="text"
                    placeholder="Detalle o nota (opcional)"
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    className={styles.manualInput}
                  />
                </div>
              </div>

              <div className={styles.manualFormFooter}>
                <div className={styles.manualPeriodStatus}>
                  {!isManualDateInActivePeriod ? (
                    <span className={styles.periodPillExternal}>
                      📂 Fecha fuera del período activo: se agrupará en el período{' '}
                      <strong>{manualOccurredAtIso.slice(0, 7)}</strong> sin distorsionar tu ciclo actual
                    </span>
                  ) : manualHorizon === 'NEXT_CYCLE' ? (
                    <span className={styles.periodPillExternal}>
                      🔵 <strong>Impacta en Próximo Ciclo:</strong> la facturación de{' '}
                      <strong>{selectedManualCard?.alias}</strong> cerró el{' '}
                      <strong>{selectedManualCardCloseDateIso}</strong> — no reduce tu disponible de este mes
                    </span>
                  ) : (
                    <span className={styles.periodPillActive}>
                      🟢 Se contabilizará en el <strong>Ciclo Actual</strong>
                      {selectedManualCardCloseDateIso
                        ? ` (puedes gastar con ${selectedManualCard?.alias} hasta el ${selectedManualCardCloseDateIso})`
                        : ''}{' '}
                      · impacto: -
                      {formatSolesFromCents(
                        manualImpact.dailyAllowanceReductionCents
                      )}
                      /día
                    </span>
                  )}
                </div>

                <button type="submit" className={styles.manualSubmitBtn}>
                  💾 Guardar Gasto Manual (
                  {formatSolesFromCents(manualImpact.proposedExpenseCents)})
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {/* 1. Cabecera + 4 Indicadores de Control de Gastos (Agrupados por Fecha de Emisión del Período Seleccionado) */}
      <section className={styles.headerCard}>
        <div className={styles.headerTop}>
          <div>
            <span className={styles.eyebrow}>
              {periodLabelText.toUpperCase()} ·{' '}
              {analytics.controllableTransactionCount} CONTROLABLES ·{' '}
              {analytics.inevitableTransactionCount} INEVITABLES
            </span>
            <h2 className={styles.title}>
              Resumen de Gastos ({periodLabelText}){' '}
              <InfoPopover title="Resumen del Período">
                Todos los montos y gráficos reflejan exclusivamente los gastos cuya <strong>Fecha de Emisión</strong> pertenece a <strong>{periodLabelText}</strong>. Los gastos marcados como <strong>🔒 Inevitables</strong> se excluyen del ranking de control.
              </InfoPopover>
            </h2>
          </div>
        </div>

        <div className={styles.kpiSummaryGrid}>
          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>1. GASTO TOTAL VS. CONTROLABLE</span>
            <span className={styles.kpiMainValue}>
              {formatSolesFromCents(analytics.totalSpentCents)}
            </span>
            <span className={styles.kpiSubValue}>
              Controlable medido: {formatSolesFromCents(analytics.controllableSpentCents)}
            </span>
            <span className={styles.kpiMeta}>
              🔒 Inevitable fijo: {formatSolesFromCents(analytics.inevitableSpentCents)}
            </span>
          </div>

          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>
              2. MAYOR CATEGORÍA CONTROLABLE
            </span>
            <span className={styles.kpiMainValue}>
              {topCategory ? topCategory.label : 'Sin gastos controlables'}
            </span>
            <span className={styles.kpiSubValue}>
              {topCategory
                ? `${formatSolesFromCents(topCategory.totalCents)} (${topCategory.shareOfTotalSpentPercent}% del controlable)`
                : 'S/ 0.00'}
            </span>
            <span className={styles.kpiMeta}>
              {topCategory
                ? `Impacta ${topCategory.shareOfSpendingCapPercent}% de tu límite permitido`
                : 'Excluye gastos inevitables'}
            </span>
          </div>

          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>
              3. ESTABLECIMIENTO TOP (CONTROLABLE)
            </span>
            <span className={styles.kpiMainValue}>
              {topMerchant ? topMerchant.merchantName : 'Sin gastos controlables'}
            </span>
            <span className={styles.kpiSubValue}>
              {topMerchant
                ? `${formatSolesFromCents(topMerchant.totalCents)} · RUC ${
                    topMerchant.issuerRuc ?? 'S/R'
                  }`
                : 'S/ 0.00'}
            </span>
            <span className={styles.kpiMeta}>
              {topMerchant
                ? `${topMerchant.transactionCount} compra(s) · -${formatSolesFromCents(
                    topMerchant.dailyCompressionCents
                  )}/día`
                : 'Excluye gastos inevitables'}
            </span>
          </div>

          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>
              4. PRODUCTO / SERVICIO DE MAYOR IMPACTO
            </span>
            <span className={styles.kpiMainValue}>
              {topProduct ? topProduct.productOrServiceName : 'Sin gastos controlables'}
            </span>
            <span className={styles.kpiSubValue}>
              {topProduct
                ? `${formatSolesFromCents(topProduct.totalCents)} (${topProduct.shareOfSpendingCapPercent}% del límite)`
                : 'S/ 0.00'}
            </span>
            <span className={styles.kpiMeta}>
              {topProduct
                ? `Comprimió -${formatSolesFromCents(
                    topProduct.dailyCompressionCents
                  )}/día (${topProduct.budgetHoursConsumed}h)`
                : 'Excluye gastos inevitables'}
            </span>
          </div>
        </div>
      </section>

      {/* 1. Listado Principal de Gastos Realizados y Comprobantes Escaneados */}
      <section
        className={styles.ledgerCard}
        aria-label="Listado de Gastos Realizados y Comprobantes"
      >
        <div className={styles.chartHeader}>
          <div>
            <h3 className={styles.chartTitle}>
              📋 Listado de Gastos Realizados ({filteredExpenses.length}){' '}
              <InfoPopover title="Listado de Gastos Realizados">
                Ordenados por <strong>Fecha de Emisión</strong> en {periodLabelText}. Los gastos registrados con cámara o imagen muestran la miniatura de la boleta o recibo y su ficha editable.
              </InfoPopover>
            </h3>
          </div>
          {onOpenScannerView && (
            <button
              type="button"
              className={styles.scanShortcutBtn}
              onClick={onOpenScannerView}
            >
              📷 Escanear Boleta
            </button>
          )}
        </div>

        <div className={styles.filterRow}>
          <input
            type="search"
            placeholder="🔎 Buscar por comercio, RUC, N° de boleta, producto o concepto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          <select
            value={sourceFilter}
            onChange={(e) =>
              setSourceFilter(
                e.target.value as
                  | 'ALL'
                  | 'WITH_RECEIPT_IMAGE'
                  | 'MANUAL_AND_RECURRING'
              )
            }
            className={styles.filterSelect}
            aria-label="Filtrar por comprobante o registro"
          >
            <option value="ALL">🧾 Todos los Gastos (Con y Sin Imagen)</option>
            <option value="WITH_RECEIPT_IMAGE">
              🖼️ Solo con Boleta / Recibo Escaneado
            </option>
            <option value="MANUAL_AND_RECURRING">
              ✍️ Solo Manuales y Recurrentes (Sin Imagen)
            </option>
          </select>
          <select
            value={controlFilter}
            onChange={(e) =>
              setControlFilter(
                e.target.value as 'ALL' | 'CONTROLLABLE_ONLY' | 'INEVITABLE_ONLY'
              )
            }
            className={styles.filterSelect}
            aria-label="Filtrar por tipo de control"
          >
            <option value="ALL">Todos (Controlables + Inevitables)</option>
            <option value="CONTROLLABLE_ONLY">🎯 Solo Gastos Controlables</option>
            <option value="INEVITABLE_ONLY">🔒 Solo Gastos Inevitables</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filterSelect}
            aria-label="Filtrar por categoría"
          >
            <option value="ALL">Todas las Categorías</option>
            {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map(
              (catKey) => (
                <option key={catKey} value={catKey}>
                  {EXPENSE_CATEGORY_LABELS[catKey]}
                </option>
              )
            )}
          </select>
          <select
            value={cardFilter}
            onChange={(e) => setCardFilter(e.target.value)}
            className={styles.filterSelect}
            aria-label="Filtrar por tarjeta de crédito"
          >
            <option value="ALL">💳 Todas las Tarjetas de Crédito</option>
            <option value="DIRECT_DEBIT_OR_CASH">
              ⚠️ Sin tarjeta de crédito asignada
            </option>
            {configuredCards.map((card) => (
              <option key={card.id} value={card.id}>
                💳 Tarjeta: {card.alias} ({card.issuerBank}
                {card.lastFourDigits ? ` · *${card.lastFourDigits}` : ''})
              </option>
            ))}
          </select>
          <select
            value={horizonFilter}
            onChange={(e) =>
              setHorizonFilter(
                e.target.value as 'ALL' | 'CURRENT_CYCLE' | 'NEXT_CYCLE'
              )
            }
            className={styles.filterSelect}
            aria-label="Filtrar por ciclo de facturación"
          >
            <option value="ALL">🔄 Todos los Horizontes (Actual + Próximo)</option>
            <option value="CURRENT_CYCLE">🟢 Solo Ciclo Actual</option>
            <option value="NEXT_CYCLE">
              🔵 Solo Próximo Ciclo (Post-Facturación)
            </option>
          </select>
        </div>

        <div className={styles.expenseList}>
          {filteredExpenses.length === 0 ? (
            <div className={styles.emptyExpensesNotice}>
              Sin gastos registrados en <strong>{periodLabelText}</strong>.
            </div>
          ) : (
            filteredExpenses.map((item) => {
              const inevitable = isExpenseInevitableInState(
                item,
                cycleState.recurringExpenses
              );
              const isEditingDate = editingExpenseDateId === item.id;
              const rawReceiptUrl = resolveExpenseReceiptImageUrl(item);
              const hasFailedImage = Boolean(failedReceiptImageIds[item.id]);
              const activeReceiptUrl =
                rawReceiptUrl && !hasFailedImage ? rawReceiptUrl : null;
              const isReceiptOrigin =
                Boolean(activeReceiptUrl) || item.source === 'RECEIPT_OCR';
              const itemCard = item.creditCardId
                ? configuredCards.find((c) => c.id === item.creditCardId) ?? null
                : null;
              const itemHorizon = item.billingCycleHorizon ?? 'CURRENT_CYCLE';

              return (
                <div
                  key={item.id}
                  className={`${styles.expenseRowCard} ${
                    isReceiptOrigin ? styles.expenseRowWithReceipt : ''
                  }`}
                >
                  <div className={styles.expenseRowMain}>
                    {/* Columna de Miniatura de Boleta / Recibo (si fue registrado con imagen o escáner) */}
                    {isReceiptOrigin && (
                      <div className={styles.receiptThumbColumn}>
                        {activeReceiptUrl ? (
                          <button
                            type="button"
                            className={styles.receiptThumbButton}
                            onClick={() => openExpenseDetailModal(item, false)}
                            title="Haz clic para ver la imagen de la boleta o recibo en tamaño completo"
                          >
                            <img
                              src={activeReceiptUrl}
                              alt={`Boleta o recibo de ${item.merchantName}`}
                              className={styles.receiptThumbImg}
                              loading="lazy"
                              onError={() =>
                                setFailedReceiptImageIds((prev) => ({
                                  ...prev,
                                  [item.id]: true,
                                }))
                              }
                            />
                            <span className={styles.receiptThumbCaption}>
                              🔍 Ver Boleta
                            </span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={styles.receiptPlaceholderButton}
                            onClick={() => openExpenseDetailModal(item, false)}
                            title="Abrir detalle del comprobante o adjuntar foto de la boleta"
                          >
                            <span className={styles.receiptPlaceholderIcon}>
                              🧾
                            </span>
                            <span className={styles.receiptPlaceholderText}>
                              Boleta IA
                            </span>
                            <span className={styles.receiptThumbCaption}>
                              📷 Adjuntar / Ver
                            </span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* Columna Central: Datos del Gasto y Detalle Completo */}
                    <div className={styles.expenseLeft}>
                      <div className={styles.expenseTitleRow}>
                        <span className={styles.expenseMerchant}>
                          {item.merchantName}
                        </span>
                        {item.issuerRuc && (
                          <span className={styles.rucBadge}>
                            RUC: {item.issuerRuc}
                          </span>
                        )}
                        {item.receiptNumber && (
                          <span className={styles.receiptNumberBadge}>
                            🧾 N° {item.receiptNumber}
                          </span>
                        )}
                        <span
                          className={`${styles.sourcePill} ${
                            item.source === 'RECURRING_RULE'
                              ? styles.sourcePillRecurring
                              : item.source === 'WEB_MANUAL'
                                ? styles.sourcePillManual
                                : ''
                          }`}
                        >
                          {formatSourceLabel(
                            item.source,
                            Boolean(activeReceiptUrl)
                          )}
                        </span>
                        {itemCard ? (
                          <span className={styles.controllableTag}>
                            💳 {itemCard.alias}
                            {itemCard.lastFourDigits
                              ? ` (*${itemCard.lastFourDigits})`
                              : ` (${itemCard.issuerBank})`}
                          </span>
                        ) : (
                          <>
                            <span className={styles.unassignedCardBadge}>
                              ⚠️ Sin tarjeta asignada
                            </span>
                            {onUpdateExpense && configuredCards.length > 0 && (
                              <select
                                value={item.creditCardId ?? ''}
                                onChange={(e) => {
                                  const nextCardId = e.target.value || null;
                                  const picked = configuredCards.find(
                                    (c) => c.id === nextCardId
                                  );
                                  onUpdateExpense(item.id, {
                                    creditCardId: nextCardId,
                                    cardLastFourDigits:
                                      picked?.lastFourDigits || null,
                                  });
                                }}
                                className={styles.quickAssignCardSelect}
                                aria-label="Asignar tarjeta de crédito al gasto"
                              >
                                <option value="">
                                  ✏️ Asignar Tarjeta...
                                </option>
                                {configuredCards.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    💳 {c.alias} ({c.issuerBank}
                                    {c.lastFourDigits
                                      ? ` · *${c.lastFourDigits}`
                                      : ''}
                                    )
                                  </option>
                                ))}
                              </select>
                            )}
                          </>
                        )}
                        {itemHorizon === 'NEXT_CYCLE' && (
                          <span className={styles.sourcePillRecurring}>
                            🔵 Próximo Ciclo (Post-Facturación · No resta hoy)
                          </span>
                        )}
                        {inevitable ? (
                          <span className={styles.inevitableTag}>
                            🔒 INEVITABLE · Excluido de ranking de control
                          </span>
                        ) : (
                          <span className={styles.controllableTag}>
                            🎯 Gasto Controlable
                          </span>
                        )}
                      </div>

                      {/* Grilla Resumen de Detalle del Gasto */}
                      <div className={styles.expenseDetailPillsRow}>
                        <span className={styles.detailMetaPill}>
                          📅 <strong>Fecha de Emisión:</strong>{' '}
                          {item.occurredAtIso.slice(0, 10)}
                        </span>
                        <span className={styles.detailMetaPill}>
                          🏷️ <strong>Categoría:</strong>{' '}
                          {EXPENSE_CATEGORY_LABELS[item.category]}
                        </span>
                        <span className={styles.detailMetaPill}>
                          📝 <strong>Concepto / Detalle:</strong>{' '}
                          {item.description || 'Sin descripción adicional'}
                        </span>

                        {(onUpdateExpense || onUpdateExpenseDate) && !isEditingDate && (
                          <button
                            type="button"
                            className={styles.editDateInlineBtn}
                            onClick={() => {
                              setEditingExpenseDateId(item.id);
                              setEditingDateValue(item.occurredAtIso.slice(0, 10));
                              setEditingAmountValue(
                                String(centsToSoles(item.amountCents))
                              );
                            }}
                          >
                            ✏️ Editar monto / fecha
                          </button>
                        )}
                      </div>

                      {isEditingDate && (onUpdateExpense || onUpdateExpenseDate) && (
                        <div className={styles.inlineDateEditorBar}>
                          {onUpdateExpense && (
                            <label
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.35rem',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                              }}
                            >
                              Monto S/
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={editingAmountValue}
                                onChange={(e) =>
                                  setEditingAmountValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const nextAmount = parsePositiveSoles(
                                      editingAmountValue,
                                      centsToSoles(item.amountCents)
                                    );
                                    const nextDate =
                                      editingDateValue.trim().slice(0, 10) ||
                                      item.occurredAtIso.slice(0, 10);
                                    onUpdateExpense(item.id, {
                                      amountSoles: nextAmount,
                                      occurredAtIso: nextDate,
                                    });
                                    setEditingExpenseDateId(null);
                                  }
                                }}
                                className={styles.inlineDateInput}
                                style={{ width: '6.8rem' }}
                                aria-label="Nuevo monto del gasto en Soles"
                              />
                            </label>
                          )}
                          <input
                            type="date"
                            value={editingDateValue}
                            onChange={(e) => setEditingDateValue(e.target.value)}
                            className={styles.inlineDateInput}
                            aria-label="Nueva fecha de emisión"
                          />
                          <button
                            type="button"
                            className={styles.inlineDateSaveBtn}
                            onClick={() => {
                              const nextDate =
                                editingDateValue.trim().slice(0, 10) ||
                                item.occurredAtIso.slice(0, 10);
                              if (onUpdateExpense) {
                                const nextAmount = parsePositiveSoles(
                                  editingAmountValue,
                                  centsToSoles(item.amountCents)
                                );
                                onUpdateExpense(item.id, {
                                  amountSoles: nextAmount,
                                  occurredAtIso: nextDate,
                                });
                              } else if (onUpdateExpenseDate && nextDate) {
                                onUpdateExpenseDate(item.id, nextDate);
                              }
                              setEditingExpenseDateId(null);
                            }}
                          >
                            💾 Guardar
                          </button>
                          <button
                            type="button"
                            className={styles.inlineDateCancelBtn}
                            onClick={() => setEditingExpenseDateId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      )}

                      {/* Ítems / Productos extraídos de la Boleta o Registrados */}
                      {item.lineItems && item.lineItems.length > 0 && (
                        <div className={styles.lineItemsSublist}>
                          <span className={styles.lineItemsLabel}>
                            🛒 Detalle de ítems ({item.lineItems.length}):
                          </span>
                          {item.lineItems.map((li, idx) => (
                            <span
                              key={`${item.id}-li-${idx}`}
                              className={styles.lineItemChip}
                            >
                              🛍️ {li.quantity}x {li.description} ·{' '}
                              <strong>{formatSolesFromCents(li.totalCents)}</strong>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Columna Derecha: Monto, Impacto y Acciones de Ver Boleta / Detalle */}
                    <div className={styles.expenseRight}>
                      <span className={styles.expenseAmount}>
                        -{formatSolesFromCents(item.amountCents)}
                      </span>
                      <span className={styles.expenseImpactTag}>
                        -{formatSolesFromCents(item.dailyCompressionCents)}/día ·{' '}
                        {item.budgetHoursConsumed}h
                      </span>

                      <div className={styles.expenseActionsRow}>
                        <button
                          type="button"
                          className={
                            activeReceiptUrl
                              ? styles.viewReceiptBtnPrimary
                              : styles.viewDetailBtnSecondary
                          }
                          onClick={() => openExpenseDetailModal(item, false)}
                        >
                          {activeReceiptUrl
                            ? '🖼️ Ver Boleta y Detalle'
                            : isReceiptOrigin
                              ? '🧾 Ver Detalle / Adjuntar Foto'
                              : '🔎 Ver Detalle del Gasto'}
                        </button>

                        {onUpdateExpense && (
                          <button
                            type="button"
                            className={styles.editExpenseRowBtn}
                            onClick={() => openExpenseDetailModal(item, true)}
                          >
                            {itemCard
                              ? '✏️ Editar Gasto'
                              : '✏️ Asignar Tarjeta / Editar'}
                          </button>
                        )}

                        {onDeleteExpense && (
                          <button
                            type="button"
                            className={styles.deleteExpenseBtn}
                            onClick={() => onDeleteExpense(item.id)}
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* 2. Gráficos Comparativos de Control: Por Categoría y Por Establecimiento (RUC / Razón Social) */}
      <div className={styles.chartsGrid}>
        {/* GRÁFICO 1: Gastos Controlables por Categoría */}
        <section
          className={styles.chartCard}
          aria-label="Gráfico de Gastos Controlables por Categoría"
        >
          <div className={styles.chartHeader}>
            <div>
              <h3 className={styles.chartTitle}>
                📊 Distribución por Categoría{' '}
                <InfoPopover title="Distribución por Categoría">
                  Distribución sobre tu gasto controlable ({formatSolesFromCents(analytics.controllableSpentCents)}) en {periodLabelText}, excluyendo gastos inevitables fijos.
                </InfoPopover>
              </h3>
            </div>
            <span className={styles.chartPill}>
              {analytics.byCategory.length} Categorías
            </span>
          </div>

          <div className={styles.donutLayout}>
            <div className={styles.donutSvgWrapper}>
              <svg width="142" height="142" viewBox="0 0 142 142">
                <circle
                  cx="71"
                  cy="71"
                  r={donutRadius}
                  fill="transparent"
                  stroke="#eff4ff"
                  strokeWidth="16"
                />
                {donutSegments.map((seg) => (
                  <circle
                    key={seg.category}
                    cx="71"
                    cy="71"
                    r={donutRadius}
                    fill="transparent"
                    stroke={seg.colorHex}
                    strokeWidth="16"
                    strokeDasharray={seg.dashArray}
                    strokeDashoffset={seg.dashOffset}
                    transform="rotate(-90 71 71)"
                  />
                ))}
              </svg>
              <div className={styles.donutCenterLabel}>
                <span className={styles.donutCenterTitle}>Controlable</span>
                <span className={styles.donutCenterAmount}>
                  {formatSolesFromCents(analytics.controllableSpentCents)}
                </span>
              </div>
            </div>

            <div className={styles.categoryBarsList}>
              {analytics.byCategory.map((cat, idx) => {
                const catColor =
                  sapphireCategoryPalette[
                    idx % sapphireCategoryPalette.length
                  ] ?? cat.colorHex;
                return (
                  <div key={cat.category} className={styles.barRowItem}>
                    <div className={styles.barRowTop}>
                      <div className={styles.barNameGroup}>
                        <span
                          className={styles.colorDot}
                          style={{ backgroundColor: catColor }}
                        />
                        <span className={styles.barLabelText}>{cat.label}</span>
                      </div>
                      <span className={styles.barAmountText}>
                        {formatSolesFromCents(cat.totalCents)}
                      </span>
                    </div>
                    <div className={styles.barTrack}>
                      <div
                        className={styles.barFill}
                        style={{
                          width: `${Math.min(100, cat.shareOfTotalSpentPercent)}%`,
                          backgroundColor: catColor,
                        }}
                      />
                    </div>
                    <div className={styles.barMetaRow}>
                      <span>
                        <strong>{cat.shareOfTotalSpentPercent}%</strong> del controlable ·{' '}
                        <strong>{cat.shareOfSpendingCapPercent}%</strong> del límite
                      </span>
                      <span>
                        -{formatSolesFromCents(cat.dailyCompressionCents)}/día (
                        {cat.transactionCount} mov.)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* GRÁFICO 2: Gastos por Establecimiento (RUC o Razón Social) */}
        <section
          className={styles.chartCard}
          aria-label="Gráfico de Gastos por Establecimiento (RUC o Razón Social)"
        >
          <div className={styles.chartHeader}>
            <div>
              <h3 className={styles.chartTitle}>
                🏢 Gasto por Establecimiento{' '}
                <InfoPopover title="Gasto por Establecimiento">
                  Ranking de comercios en gastos controlables agrupados por RUC o Razón Social durante {periodLabelText}.
                </InfoPopover>
              </h3>
            </div>
            <span className={styles.chartPill}>
              {analytics.byMerchant.length} Establecimientos
            </span>
          </div>

          <div className={styles.categoryBarsList}>
            {analytics.byMerchant.map((merch) => (
              <div key={merch.merchantKey} className={styles.barRowItem}>
                <div className={styles.barRowTop}>
                  <div className={styles.barNameGroup}>
                    <span className={styles.barLabelText}>{merch.merchantName}</span>
                    <span className={styles.rucBadge}>
                      RUC: {merch.issuerRuc ?? 'Sin RUC'}
                    </span>
                  </div>
                  <span className={styles.barAmountText}>
                    {formatSolesFromCents(merch.totalCents)}
                  </span>
                </div>
                <div className={styles.barTrack}>
                  <div
                    className={styles.barFill}
                    style={{
                      width: `${Math.min(100, merch.shareOfTotalSpentPercent)}%`,
                      background:
                        'linear-gradient(90deg, #00236f 0%, #006a61 100%)',
                    }}
                  />
                </div>
                <div className={styles.barMetaRow}>
                  <span>
                    {EXPENSE_CATEGORY_LABELS[merch.primaryCategory]} ·{' '}
                    <strong>{merch.shareOfTotalSpentPercent}%</strong> del controlable (
                    {merch.shareOfSpendingCapPercent}% del límite)
                  </span>
                  <span>
                    {merch.transactionCount} comp. · -
                    {formatSolesFromCents(merch.dailyCompressionCents)}/día
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 3. GRÁFICO 3: Impacto en el Gasto de un Producto o Servicio Consumido */}
      <section
        className={styles.productImpactSection}
        aria-label="Impacto por Producto o Servicio Consumido"
      >
        <div className={styles.chartHeader}>
          <div>
            <h3 className={styles.chartTitle}>
              🛍️ Impacto por Producto Consumido{' '}
              <InfoPopover title="Impacto por Producto Consumido">
                Mide el impacto de cada producto o servicio controlable en tu Límite de Gasto, Cuota Diaria y Horas de Presupuesto.
              </InfoPopover>
            </h3>
          </div>
          <span className={styles.chartPill}>
            {analytics.byProductOrService.length} Ítems
          </span>
        </div>

        <div className={styles.productSpotlightGrid}>
          {activeProduct && (
            <div className={styles.spotlightCard}>
              <label htmlFor="product-impact-select" className={styles.selectLabel}>
                🔍 Selecciona un Producto o Servicio Consumido:
              </label>
              <select
                id="product-impact-select"
                className={styles.selectInput}
                value={activeProduct.itemKey}
                onChange={(e) => setSelectedProductKey(e.target.value)}
              >
                {analytics.byProductOrService.map((item) => (
                  <option key={item.itemKey} value={item.itemKey}>
                    {item.productOrServiceName} — {formatSolesFromCents(item.totalCents)} (
                    {item.merchantName})
                  </option>
                ))}
              </select>

              <div className={styles.spotlightStatsGrid}>
                <div className={styles.spotlightMetricBox}>
                  <span className={styles.kpiLabel}>MONTO CONSUMIDO</span>
                  <span className={styles.spotlightMetricVal}>
                    {formatSolesFromCents(activeProduct.totalCents)}
                  </span>
                  <span className={styles.kpiMeta}>
                    Cantidad: {activeProduct.quantityTotal} und.
                  </span>
                </div>

                <div className={styles.spotlightMetricBox}>
                  <span className={styles.kpiLabel}>IMPACTO EN TU LÍMITE</span>
                  <span
                    className={styles.spotlightMetricVal}
                    style={{ color: '#006a61' }}
                  >
                    {activeProduct.shareOfSpendingCapPercent}% del límite
                  </span>
                  <span className={styles.kpiMeta}>
                    {activeProduct.shareOfTotalSpentPercent}% del gasto controlable
                  </span>
                </div>

                <div className={styles.spotlightMetricBox}>
                  <span className={styles.kpiLabel}>COMPRESIÓN CUOTA DIARIA</span>
                  <span
                    className={styles.spotlightMetricVal}
                    style={{ color: '#b45309' }}
                  >
                    -{formatSolesFromCents(activeProduct.dailyCompressionCents)}/día
                  </span>
                  <span className={styles.kpiMeta}>
                    Por {snapshot.remainingDays} días restantes
                  </span>
                </div>

                <div className={styles.spotlightMetricBox}>
                  <span className={styles.kpiLabel}>TIEMPO PRESUPUESTARIO</span>
                  <span
                    className={styles.spotlightMetricVal}
                    style={{ color: '#004469' }}
                  >
                    {activeProduct.budgetHoursConsumed} horas
                  </span>
                  <span className={styles.kpiMeta}>
                    {(activeProduct.budgetHoursConsumed / 24).toFixed(2)} días de cuota
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className={styles.categoryBarsList}>
            {analytics.byProductOrService.slice(0, 8).map((prod) => {
              const isSelected = activeProduct?.itemKey === prod.itemKey;
              return (
                <div
                  key={prod.itemKey}
                  className={styles.barRowItem}
                  style={
                    isSelected
                      ? { borderColor: '#00236f', cursor: 'pointer' }
                      : { cursor: 'pointer' }
                  }
                  onClick={() => setSelectedProductKey(prod.itemKey)}
                >
                  <div className={styles.barRowTop}>
                    <div className={styles.barNameGroup}>
                      <span className={styles.barLabelText}>
                        {prod.productOrServiceName}
                      </span>
                    </div>
                    <span className={styles.barAmountText}>
                      {formatSolesFromCents(prod.totalCents)}
                    </span>
                  </div>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${Math.min(100, prod.shareOfTotalSpentPercent)}%`,
                        background:
                          'linear-gradient(90deg, #00236f 0%, #006a61 100%)',
                      }}
                    />
                  </div>
                  <div className={styles.barMetaRow}>
                    <span>
                      {prod.merchantName}{' '}
                      {prod.issuerRuc ? `(RUC ${prod.issuerRuc})` : ''}
                    </span>
                    <span>
                      <strong>{prod.shareOfTotalSpentPercent}%</strong> controlable · -
                      {formatSolesFromCents(prod.dailyCompressionCents)}/día
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modal de Detalle Completo del Gasto y Visor de Imagen de Boleta / Recibo */}
      {selectedDetailExpense &&
        (() => {
          const modalRawReceiptUrl = resolveExpenseReceiptImageUrl(
            selectedDetailExpense
          );
          const modalImageFailed = Boolean(
            failedReceiptImageIds[selectedDetailExpense.id]
          );
          const modalReceiptUrl =
            modalRawReceiptUrl && !modalImageFailed ? modalRawReceiptUrl : null;
          const modalInevitable = isExpenseInevitableInState(
            selectedDetailExpense,
            cycleState.recurringExpenses
          );

          return (
            <div
              className={styles.receiptModalBackdrop}
              role="dialog"
              aria-modal="true"
              aria-label={`Detalle de gasto y comprobante de ${selectedDetailExpense.merchantName}`}
              onClick={() => setSelectedDetailExpenseId(null)}
            >
              <div
                className={styles.receiptModalDialog}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.receiptModalHeader}>
                  <div className={styles.receiptModalTitleGroup}>
                    <span className={styles.receiptModalEyebrow}>
                      {formatSourceLabel(
                        selectedDetailExpense.source,
                        Boolean(modalReceiptUrl)
                      )}{' '}
                      · FECHA DE EMISIÓN:{' '}
                      {selectedDetailExpense.occurredAtIso.slice(0, 10)}
                    </span>
                    <h3 className={styles.receiptModalTitle}>
                      {selectedDetailExpense.merchantName}
                    </h3>
                  </div>

                  <div className={styles.receiptModalHeaderRight}>
                    <span className={styles.receiptModalAmountBadge}>
                      -{formatSolesFromCents(selectedDetailExpense.amountCents)}
                    </span>
                    <button
                      type="button"
                      className={styles.receiptModalCloseBtn}
                      onClick={() => setSelectedDetailExpenseId(null)}
                    >
                      ✕ Cerrar
                    </button>
                  </div>
                </div>

                <div className={styles.receiptModalBodyGrid}>
                  {/* Panel Izquierdo: Imagen de la Boleta / Recibo Escaneado */}
                  <div className={styles.receiptModalImagePanel}>
                    <div className={styles.receiptModalSectionHeader}>
                      <span className={styles.receiptModalSectionTitle}>
                        🖼️ Imagen de la Boleta o Recibo
                      </span>
                      {modalReceiptUrl && (
                        <div className={styles.receiptImageControls}>
                          <button
                            type="button"
                            className={styles.receiptZoomBtn}
                            onClick={() => setIsReceiptZoomed((prev) => !prev)}
                          >
                            {isReceiptZoomed
                              ? '🔽 Ajustar a pantalla'
                              : '🔍 Ampliar Zoom'}
                          </button>
                          <a
                            href={modalReceiptUrl}
                            download={`boleta-${selectedDetailExpense.receiptNumber ?? selectedDetailExpense.id}.jpg`}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.receiptDownloadLink}
                          >
                            📥 Abrir / Descargar
                          </a>
                        </div>
                      )}
                    </div>

                    {modalReceiptUrl ? (
                      <div
                        className={`${styles.receiptFullImageViewport} ${
                          isReceiptZoomed
                            ? styles.receiptFullImageViewportZoomed
                            : ''
                        }`}
                      >
                        <img
                          src={modalReceiptUrl}
                          alt={`Comprobante escaneado de ${selectedDetailExpense.merchantName}`}
                          className={
                            isReceiptZoomed
                              ? styles.receiptFullImgZoomed
                              : styles.receiptFullImg
                          }
                          onClick={() => setIsReceiptZoomed((prev) => !prev)}
                          onError={() =>
                            setFailedReceiptImageIds((prev) => ({
                              ...prev,
                              [selectedDetailExpense.id]: true,
                            }))
                          }
                        />
                      </div>
                    ) : (
                      <div className={styles.noReceiptImageBox}>
                        <span className={styles.noReceiptIcon}>🧾</span>
                        <p className={styles.noReceiptTitle}>
                          Sin imagen adjunta.
                        </p>
                      </div>
                    )}

                    {onAttachExpenseReceiptImage && (
                      <div className={styles.attachReceiptBar}>
                        <label className={styles.attachReceiptLabelBtn}>
                          {attachingImageExpenseId === selectedDetailExpense.id
                            ? '⏳ Procesando imagen...'
                            : modalReceiptUrl
                              ? '📷 Cambiar Foto de Boleta / Recibo'
                              : '📷 Adjuntar Foto de Boleta / Recibo'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(event) =>
                              void handleAttachFileToExpense(
                                selectedDetailExpense.id,
                                event
                              )
                            }
                          />
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Panel Derecho: Detalle Completo Estructurado del Gasto */}
                  <div className={styles.receiptModalDetailsPanel}>
                    <div className={styles.receiptModalSectionHeader}>
                      <span className={styles.receiptModalSectionTitle}>
                        📑 Ficha Detallada del Gasto Realizado
                      </span>

                      {onUpdateExpense && (
                        <div className={styles.receiptImageControls}>
                          {isEditingDetailModal ? (
                            <>
                              <button
                                type="button"
                                className={styles.saveDetailEditsBtn}
                                onClick={handleSaveExpenseDetailEdits}
                              >
                                💾 Guardar Cambios del Gasto
                              </button>
                              <button
                                type="button"
                                className={styles.cancelDetailEditsBtn}
                                onClick={() => setIsEditingDetailModal(false)}
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={styles.editDetailToggleBtn}
                              onClick={() =>
                                openExpenseDetailModal(
                                  selectedDetailExpense,
                                  true
                                )
                              }
                            >
                              ✏️ Editar Gasto
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className={styles.detailFieldsGrid}>
                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          ESTABLECIMIENTO / EMISOR
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="text"
                            value={editMerchantName}
                            onChange={(e) =>
                              setEditMerchantName(e.target.value)
                            }
                            className={styles.detailFieldEditInput}
                          />
                        ) : (
                          <span className={styles.detailFieldValue}>
                            {selectedDetailExpense.merchantName}
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          RUC DEL EMISOR
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="text"
                            value={editIssuerRuc}
                            placeholder="Sin RUC"
                            onChange={(e) => setEditIssuerRuc(e.target.value)}
                            className={styles.detailFieldEditInput}
                          />
                        ) : (
                          <span className={styles.detailFieldValueMono}>
                            {selectedDetailExpense.issuerRuc ??
                              'Sin RUC registrado'}
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          N° DE BOLETA / COMPROBANTE
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="text"
                            value={editReceiptNumber}
                            placeholder="Sin N° de comprobante"
                            onChange={(e) =>
                              setEditReceiptNumber(e.target.value)
                            }
                            className={styles.detailFieldEditInput}
                          />
                        ) : (
                          <span className={styles.detailFieldValueMono}>
                            {selectedDetailExpense.receiptNumber ??
                              'Sin N° de comprobante'}
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          FECHA DE EMISIÓN CONTABLE
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="date"
                            value={editOccurredAtIso}
                            onChange={(e) =>
                              setEditOccurredAtIso(e.target.value)
                            }
                            className={styles.detailFieldEditInput}
                          />
                        ) : (
                          <span className={styles.detailFieldValueMono}>
                            📅 {selectedDetailExpense.occurredAtIso.slice(0, 10)}
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          CATEGORÍA PRESUPUESTARIA
                        </span>
                        {isEditingDetailModal ? (
                          <select
                            value={editCategory}
                            onChange={(e) =>
                              setEditCategory(e.target.value as ExpenseCategory)
                            }
                            className={styles.detailFieldEditSelect}
                          >
                            {(
                              Object.keys(
                                EXPENSE_CATEGORY_LABELS
                              ) as ExpenseCategory[]
                            ).map((catKey) => (
                              <option key={catKey} value={catKey}>
                                {EXPENSE_CATEGORY_LABELS[catKey]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={styles.detailFieldValue}>
                            {
                              EXPENSE_CATEGORY_LABELS[
                                selectedDetailExpense.category
                              ]
                            }
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          CLASIFICACIÓN DE CONTROL
                        </span>
                        {isEditingDetailModal ? (
                          <select
                            value={
                              editIsInevitable ? 'INEVITABLE' : 'CONTROLLABLE'
                            }
                            onChange={(e) =>
                              setEditIsInevitable(
                                e.target.value === 'INEVITABLE'
                              )
                            }
                            className={styles.detailFieldEditSelect}
                          >
                            <option value="CONTROLLABLE">
                              🎯 Gasto Controlable
                            </option>
                            <option value="INEVITABLE">
                              🔒 Gasto Inevitable Fijo
                            </option>
                          </select>
                        ) : (
                          <span className={styles.detailFieldValue}>
                            {modalInevitable
                              ? '🔒 Gasto Inevitable Fijo'
                              : '🎯 Gasto Controlable'}
                          </span>
                        )}
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          TARJETA DE CRÉDITO UTILIZADA
                        </span>
                        <select
                          value={
                            isEditingDetailModal
                              ? editCreditCardId
                              : (selectedDetailExpense.creditCardId ?? '')
                          }
                          onChange={(e) => {
                            const newCardId = e.target.value;
                            setEditCreditCardId(newCardId);
                            if (!isEditingDetailModal) {
                              const picked = configuredCards.find(
                                (c) => c.id === newCardId
                              );
                              onUpdateExpense?.(selectedDetailExpense.id, {
                                creditCardId: newCardId || null,
                                cardLastFourDigits:
                                  picked?.lastFourDigits || null,
                              });
                            }
                          }}
                          className={styles.detailFieldEditSelect}
                          aria-label="Tarjeta de crédito utilizada en este gasto"
                        >
                          <option value="">
                            ⚠️ Sin tarjeta asignada — Seleccionar tarjeta
                          </option>
                          {configuredCards.map((card) => (
                            <option key={card.id} value={card.id}>
                              💳 {card.alias} ({card.issuerBank}
                              {card.lastFourDigits
                                ? ` · *${card.lastFourDigits}`
                                : ''}
                              )
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={styles.detailFieldCard}>
                        <span className={styles.detailFieldLabel}>
                          HORIZONTE DE FACTURACIÓN
                        </span>
                        <span className={styles.detailFieldValue}>
                          {(() => {
                            const effectiveHorizon = isEditingDetailModal
                              ? classifyExpenseHorizonForCardInCycle(
                                  cycleState,
                                  editOccurredAtIso ||
                                    selectedDetailExpense.occurredAtIso,
                                  editCreditCardId || null
                                )
                              : (selectedDetailExpense.billingCycleHorizon ??
                                'CURRENT_CYCLE');
                            return effectiveHorizon === 'NEXT_CYCLE'
                              ? '🔵 Próximo Ciclo (Post-Facturación)'
                              : '🟢 Ciclo Actual';
                          })()}
                        </span>
                      </div>

                      <div className={styles.detailFieldCardFull}>
                        <span className={styles.detailFieldLabel}>
                          MONTO TOTAL EN SOLES (PEN)
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={editAmountSoles}
                            onChange={(e) => setEditAmountSoles(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveExpenseDetailEdits();
                              }
                            }}
                            className={styles.detailFieldEditInput}
                            aria-label="Editar monto total del gasto en Soles"
                          />
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '0.5rem',
                            }}
                          >
                            <span className={styles.detailFieldValueMono}>
                              {formatSolesFromCents(
                                selectedDetailExpense.amountCents
                              )}
                            </span>
                            {onUpdateExpense && (
                              <button
                                type="button"
                                className={styles.editDateInlineBtn}
                                onClick={() =>
                                  openExpenseDetailModal(
                                    selectedDetailExpense,
                                    true
                                  )
                                }
                              >
                                ✏️ Editar monto
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className={styles.detailFieldCardFull}>
                        <span className={styles.detailFieldLabel}>
                          CONCEPTO / DESCRIPCIÓN DEL GASTO
                        </span>
                        {isEditingDetailModal ? (
                          <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveExpenseDetailEdits();
                              }
                            }}
                            className={styles.detailFieldEditInput}
                          />
                        ) : (
                          <span className={styles.detailFieldValue}>
                            {selectedDetailExpense.description ||
                              'Sin nota adicional'}
                          </span>
                        )}
                      </div>

                      {isEditingDetailModal && (
                        <div className={styles.detailFieldCardFull}>
                          <div className={styles.detailEditActionsBar}>
                            <button
                              type="button"
                              className={styles.saveDetailEditsBtn}
                              onClick={handleSaveExpenseDetailEdits}
                            >
                              💾 Guardar Cambios del Gasto
                            </button>
                            <button
                              type="button"
                              className={styles.cancelDetailEditsBtn}
                              onClick={() => setIsEditingDetailModal(false)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Impacto Financiero y Temporal */}
                    {(() => {
                      const previewSoles = isEditingDetailModal
                        ? parsePositiveSoles(
                            editAmountSoles,
                            centsToSoles(selectedDetailExpense.amountCents)
                          )
                        : centsToSoles(selectedDetailExpense.amountCents);
                      const previewImpact = simulateExpenseImpact(
                        snapshot,
                        previewSoles
                      );
                      const previewCents = isEditingDetailModal
                        ? Math.round(previewSoles * 100)
                        : selectedDetailExpense.amountCents;
                      const previewDailyCents = isEditingDetailModal
                        ? previewImpact.dailyAllowanceReductionCents
                        : selectedDetailExpense.dailyCompressionCents;
                      const previewHours = isEditingDetailModal
                        ? previewImpact.budgetTimeCostHours
                        : selectedDetailExpense.budgetHoursConsumed;

                      return (
                        <div className={styles.modalImpactBanner}>
                          <div className={styles.modalImpactCol}>
                            <span className={styles.detailFieldLabel}>
                              MONTO TOTAL
                            </span>
                            {isEditingDetailModal ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.35rem',
                                }}
                              >
                                <span className={styles.modalImpactAmount}>
                                  -S/
                                </span>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={editAmountSoles}
                                  onChange={(e) =>
                                    setEditAmountSoles(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveExpenseDetailEdits();
                                    }
                                  }}
                                  className={styles.detailFieldEditInput}
                                  style={{ maxWidth: '8rem', fontWeight: 800 }}
                                  aria-label="Monto total editable en Soles"
                                />
                              </div>
                            ) : (
                              <span className={styles.modalImpactAmount}>
                                -{formatSolesFromCents(previewCents)}
                              </span>
                            )}
                          </div>
                          <div className={styles.modalImpactCol}>
                            <span className={styles.detailFieldLabel}>
                              COMPRESIÓN CUOTA DIARIA
                            </span>
                            <span className={styles.modalImpactWarning}>
                              -{formatSolesFromCents(previewDailyCents)}/día
                            </span>
                          </div>
                          <div className={styles.modalImpactCol}>
                            <span className={styles.detailFieldLabel}>
                              COSTO EN HORAS DE PRESUPUESTO
                            </span>
                            <span className={styles.modalImpactInfo}>
                              {previewHours} horas
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tabla de Productos / Ítems del Comprobante */}
                    <div className={styles.modalItemsSection}>
                      <span className={styles.receiptModalSectionTitle}>
                        🛒 Desglose de Productos o Servicios Consumidos
                      </span>
                      {selectedDetailExpense.lineItems &&
                      selectedDetailExpense.lineItems.length > 0 ? (
                        <div className={styles.modalItemsTable}>
                          <div className={styles.modalItemsHeaderRow}>
                            <span>Cant.</span>
                            <span>Producto / Servicio</span>
                            <span>P. Unit.</span>
                            <span>Subtotal</span>
                          </div>
                          {selectedDetailExpense.lineItems.map((li, idx) => (
                            <div
                              key={`${selectedDetailExpense.id}-modal-li-${idx}`}
                              className={styles.modalItemsRow}
                            >
                              <span className={styles.modalItemQty}>
                                {li.quantity}x
                              </span>
                              <span className={styles.modalItemDesc}>
                                {li.description}
                              </span>
                              <span className={styles.modalItemUnit}>
                                {formatSolesFromCents(
                                  Math.round(
                                    li.totalCents / Math.max(1, li.quantity)
                                  )
                                )}
                              </span>
                              <span className={styles.modalItemTotal}>
                                {formatSolesFromCents(li.totalCents)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.modalItemsEmpty}>
                          <span>
                            1x {selectedDetailExpense.description || selectedDetailExpense.merchantName} —{' '}
                            <strong>
                              {formatSolesFromCents(
                                selectedDetailExpense.amountCents
                              )}
                            </strong>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
